/**
 * Page "Soutiens institutionnels" — sémantiquement riche pour le référencement.
 *
 * Rassemble les personnalités et institutions engagées aux côtés du Prieuré :
 * Présidence de la République, Région Normandie (Hervé Morin), Département
 * de la Manche (Aymeric Chauchat), Atout France (Thomas Velter), partenaires
 * du Millenium 2027 en baie du Mont-Saint-Michel.
 *
 * Chaque bloc a un H2/H3 dédié + JSON-LD via SeoHead pour maximiser la
 * visibilité Google des noms propres.
 */
import Ornament from "@/components/Ornament";
import SeoHead from "@/components/SeoHead";

const SEO_TITLES = {
  fr: "Soutiens institutionnels — Hervé Morin, Aymeric Chauchat, Thomas Velter, Atout France, Présidence de la République",
  en: "Institutional patrons — Hervé Morin, Aymeric Chauchat, Thomas Velter, Atout France, Presidency of the French Republic",
};

const SEO_DESC = {
  fr: "Le Prieuré saint Léonard, en baie du Mont-Saint-Michel, se prépare au Millenium 2027 avec le soutien de la Présidence de la République, de la Région Normandie (Hervé Morin), du Département de la Manche (Aymeric Chauchat), d'Atout France (Thomas Velter) et du label de production Viva Artist.",
  en: "The Priory of Saint Léonard, in the bay of Mont-Saint-Michel, is preparing for its Millennium 2027 with the support of the Presidency of the French Republic, the Normandy Region (Hervé Morin), the Department of Manche (Aymeric Chauchat), Atout France (Thomas Velter) and the Viva Artist label.",
};

const PATRONS = [
  {
    id: "presidence",
    title: "Présidence de la République française",
    role: "Haut patronage",
    body:
      "Le Millenium 2027 des Fêtes populaires de saint Léonard est placé sous le haut patronage de la Présidence de la République française — reconnaissance d'un événement national mêlant patrimoine, culture et diplomatie européenne.",
    href: "https://www.elysee.fr",
  },
  {
    id: "morin",
    title: "Hervé Morin",
    role: "Président de la Région Normandie",
    body:
      "Sous l'impulsion d'Hervé Morin, président du Conseil régional de Normandie, la Région Normandie soutient le Millenium 2027 dans le cadre de l'Année Européenne des Normands. Le Prieuré saint Léonard, sur son promontoire dominant la baie du Mont-Saint-Michel, est l'un des lieux emblématiques du programme régional.",
    href: "https://www.normandie.fr",
  },
  {
    id: "chauchat",
    title: "Aymeric Chauchat",
    role: "Département de la Manche",
    body:
      "Aymeric Chauchat, engagé auprès du Département de la Manche, accompagne le projet du Prieuré saint Léonard, à Vains. Le Département soutient le rayonnement culturel de la baie du Mont-Saint-Michel et la sauvegarde des monuments historiques de la façade maritime normande.",
    href: "https://www.manche.fr",
  },
  {
    id: "velter",
    title: "Thomas Velter",
    role: "Atout France · Agence de développement touristique de la France",
    body:
      "Thomas Velter, directeur général délégué d'Atout France, associe l'opérateur national de l'ingénierie touristique au Millenium 2027 du Prieuré saint Léonard. La baie du Mont-Saint-Michel figure parmi les destinations phares de la promotion internationale du tourisme culturel et spirituel français.",
    href: "https://www.atout-france.fr",
  },
  {
    id: "viva-artist",
    title: "Viva Artist",
    role: "Label de production du Prieuré",
    body:
      "Viva Artist est le label de production tous médias du Prieuré saint Léonard — musique, chant, cinéma, image, installation. Il accompagne les artistes qui sculptent la lumière et le souffle pour donner corps aux œuvres nées en résidence, en Normandie.",
    href: "/viva-artist",
  },
];

export default function SoutiensInstitutionnels() {
  return (
    <>
      <SeoHead titles={SEO_TITLES} descriptions={SEO_DESC} image="/photos/prieure-meadow.jpg" />
      <div data-testid="soutiens-page" style={{ background: "var(--parchment)" }}>
        {/* HERO */}
        <section className="py-16 sm:py-20 px-6 text-center border-b" style={{ borderColor: "var(--gold)" }}>
          <Ornament />
          <p className="font-caps text-[0.72rem] mt-6" style={{ color: "var(--gold-dark, #a68b3d)", letterSpacing: "0.2em" }}>
            MILLENIUM · 2027 · BAIE DU MONT-SAINT-MICHEL
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">
            Soutiens institutionnels
          </h1>
          <p className="font-body italic text-base sm:text-lg mt-5 max-w-3xl mx-auto" style={{ color: "var(--muted)" }}>
            Le Prieuré saint Léonard, en baie du Mont-Saint-Michel, se prépare à célébrer son Millenium 2027 avec le soutien de personnalités publiques et d'institutions culturelles engagées à ses côtés.
          </p>
        </section>

        {/* PATRONS list — each block SEO-optimised */}
        <section className="max-w-4xl mx-auto px-6 py-16 space-y-14">
          {PATRONS.map((p, i) => (
            <article
              key={p.id}
              id={p.id}
              data-testid={`patron-${p.id}`}
              className="border-l-2 pl-6"
              style={{ borderColor: i % 2 === 0 ? "var(--cinnabar)" : "var(--gold)" }}
            >
              <div className="font-caps text-[0.65rem] mb-2" style={{ color: "var(--gold-dark, #a68b3d)", letterSpacing: "0.18em" }}>
                {p.role}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mb-3" style={{ color: "var(--ink)" }}>
                {p.title}
              </h2>
              <p className="font-body text-lg leading-relaxed mb-3" style={{ color: "var(--ink-2)" }}>
                {p.body}
              </p>
              {p.href && (
                <a
                  href={p.href}
                  target={p.href.startsWith("http") ? "_blank" : undefined}
                  rel={p.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="font-caps text-[0.7rem]"
                  style={{ color: "var(--cinnabar)", letterSpacing: "0.16em" }}
                  data-testid={`patron-link-${p.id}`}
                >
                  {p.href.startsWith("http") ? "→ Site officiel" : "→ En savoir plus"}
                </a>
              )}
            </article>
          ))}
        </section>

        {/* Semantic footer block reinforcing the priory context */}
        <section className="py-16 px-6 border-t" style={{ borderColor: "var(--gold)", background: "var(--parchment-2, #f4ecd8)" }}>
          <div className="max-w-3xl mx-auto text-center">
            <Ornament />
            <h2 className="font-display text-2xl sm:text-3xl mt-6 mb-4">
              Un rayonnement européen ancré en Normandie
            </h2>
            <p className="font-body text-base leading-relaxed" style={{ color: "var(--ink-2)" }}>
              Depuis les Mérovingiens, le Prieuré saint Léonard, à Vains, veille sur la baie du Mont-Saint-Michel.
              En 2027, à l'occasion de son millénaire, il devient l'un des lieux d'accueil de l'Année Européenne des Normands.
              Les Fêtes populaires de saint Léonard — <em>Libération · Lumière · Unité</em> — se tiendront du 1ᵉʳ juillet (17 h) au 5 juillet 2027 (11 h),
              en présence de personnalités et de délégations de plusieurs pays européens.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
